import { inject, Injectable } from '@angular/core';
import type { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Router } from '@angular/router';
import { UserApiService } from '@dev-fast/backend-services';
import { MAIN_LANG } from '@dev-fast/types';
import type { Observable } from 'rxjs';
import { catchError, map, of, switchMap, take } from 'rxjs';

import { EnvironmentService } from '@app/core/environment-service';
import { LanguageService } from '@app/core/language-service';
import { UserService } from '@app/core/user-service';
import { linkTransform } from '@app/shared/utils';

import { WelcomeEntryPointService } from './welcome-entry-point.service';

@Injectable({
  providedIn: 'root',
})
export class WelcomeWorkDeactivateGuard implements CanActivate {
  readonly #userService = inject(UserService);
  readonly #router = inject(Router);
  readonly #environmentService = inject(EnvironmentService);
  readonly #languageService = inject(LanguageService);
  readonly #welcomeEntryPointService = inject(WelcomeEntryPointService);
  readonly #apiUserService = inject(UserApiService);

  readonly isActiveWelcomeLayout = this.#environmentService.isStore;

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // Если WELCOME_LAYOUT выключен, разрешаем доступ к основному приложению
    if (!this.isActiveWelcomeLayout) {
      return true;
    }

    // Если WELCOME_LAYOUT включен, проверяем авторизацию
    return this.#languageService.currentLocaleCode$.pipe(
      take(1),
      switchMap((code) => {
        return this.#userService.user$.pipe(
          take(1),
          switchMap((user) => {
            if (user !== undefined) {
              return of(user);
            }
            return this.#apiUserService.getMeProfile({ detailed: false });
          }),
          map((user) => {
            if (user) {
              return true;
            }
            return this.#createLandingUrlTree(code);
          }),
          catchError(() => of(this.#createLandingUrlTree(code))),
        );
      }),
    );
  }

  #createLandingUrlTree(code: string): UrlTree {
    const redirectPath = this.#welcomeEntryPointService.getRedirectPath();

    if (code === MAIN_LANG) {
      return this.#router.createUrlTree([`/${redirectPath}`]);
    }

    const localizedLink = linkTransform(redirectPath, code);

    return this.#router.createUrlTree([`/${localizedLink}`]);
  }
}
